import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Collapse, List, ListItem, makeStyles, Popover, Typography, useMediaQuery, useTheme, } from '@material-ui/core';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { For } from 'react-loops';
import { useLocationActive } from '../../../hooks/useLocationActive';
import { appHeaderHeight, topNavHeight, transitionSpeed } from '../../../theme';
import { PrimaryLink } from './PrimaryLink';
import { SubLink } from './SubLink';
const useStyles = makeStyles((theme) => ({
    collapseContainer: {
        display: 'none',
        flexBasis: '100%',
        padding: theme.spacing(0, 0, 0, 5),
        transition: `padding ${transitionSpeed} ease-out`,
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
    },
    collapseContainerCollapsed: {
        display: 'none',
        padding: theme.spacing(0, 0, 0, 6),
    },
    collapseContainerSecondary: {
        padding: 0,
    },
    navItem: {
        color: '#424242',
        flexWrap: 'wrap',
        padding: theme.spacing(0),
    },
    popoverContent: {
        height: 'calc(100% - 45px)',
        overflow: 'hidden',
        overflowY: 'auto',
        padding: theme.spacing(0, 1, 0),
    },
    popoverPaper: {
        height: `calc(100vh - ${appHeaderHeight}px - ${topNavHeight}px)`,
        left: '72px !important',
        minWidth: 250,
        overflow: 'visible',
        top: `calc(${appHeaderHeight}px + ${topNavHeight}px) !important`,
        '& .chevron': {
            display: 'none',
        },
    },
    subLinkHeader: {
        fontWeight: 600,
        padding: theme.spacing(2, 2, 1),
    },
}));
/**
 * Left navigation item.
 * If `props.sublinks` is present, the `PopOver` will appear when the left navigation is collapsed
 * The `Collapse` section will appear when the left navigation is open.
 *
 * @param {React.ReactNode} props.icon - The icon for the left navigation item.
 * @param {string} props.id - The ID of the left navigation item.
 * @param {boolean} props.isMenuOpen - Indicates whether the menu is open or not.
 * @param {string} props.text - The text for the left navigation item.
 * @param {Array<LeftNavLink>} props.subLinks - The sublinks for the left navigation item.
 * @param {string} props.tooltipText - The tooltip text for the left navigation item.
 * @returns {JSX.Element} The rendered `LeftNavItem` component.
 */
export function LeftNavItem({ icon, id, isMenuOpen, shouldShowDot, status, text, url, subLinks, testId, tooltipText, }) {
    const classes = useStyles();
    const theme = useTheme();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isSubCollapsed, setIsSubCollapsed] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const { pathname, isActive, isAnySubLinkActive } = useLocationActive();
    const hasSublinks = subLinks !== undefined;
    const popoverOpen = Boolean(anchorEl);
    const mediumScreenAndUp = useMediaQuery(theme.breakpoints.up('sm'));
    const onButtonClick = (e) => {
        onPopoverClose();
        if (isActive(url)) {
            e.preventDefault();
            setIsCollapsed(true);
            if (!isMenuOpen) {
                setAnchorEl(e.currentTarget);
            }
            return;
        }
        if (isAnySubLinkActive(subLinks) && !isMenuOpen) {
            e.preventDefault();
        }
        if (hasSublinks) {
            if (isMenuOpen && mediumScreenAndUp) {
                setIsCollapsed(!isCollapsed);
            }
            else if (!isMenuOpen) {
                e.preventDefault();
                setAnchorEl(e.currentTarget);
            }
        }
    };
    const onTertiaryButtonClick = () => {
        onPopoverClose();
    };
    const onPopoverClose = () => {
        if (anchorEl) {
            setAnchorEl(null);
        }
    };
    useEffect(() => {
        if (!mediumScreenAndUp || (isCollapsed && !isMenuOpen) || (isSubCollapsed && !isMenuOpen)) {
            setIsCollapsed(false);
            setIsSubCollapsed(false);
        }
    }, [isMenuOpen, mediumScreenAndUp]);
    useEffect(() => {
        setIsCollapsed(false);
        setIsSubCollapsed(false);
    }, [pathname]);
    return (_jsxs(ListItem, { className: classes.navItem, component: 'li', children: [_jsx(PrimaryLink, { hasSublinks: hasSublinks, icon: icon, id: id, isActive: hasSublinks ? isAnySubLinkActive(subLinks) : isActive(url), isCollapsed: isCollapsed, isMenuOpen: isMenuOpen, shouldShowDot: shouldShowDot, status: status, text: text, testId: testId, url: url, tooltipText: tooltipText, onClick: onButtonClick }), hasSublinks && (_jsxs(_Fragment, { children: [_jsx(Collapse, { className: clsx(classes.collapseContainer, 'collapseContainer', {
                            [classes.collapseContainerCollapsed]: !isMenuOpen,
                        }), in: isAnySubLinkActive(subLinks) || isCollapsed, children: _jsx(List, { disablePadding: true, children: _jsx(For, { of: subLinks, as: (link) => {
                                    return (_jsxs(_Fragment, { children: [_jsx(SubLink, { isCollapsed: isSubCollapsed, isMenuOpen: isMenuOpen, onClick: onPopoverClose, ...link }), _jsx(Collapse, { className: clsx(classes.collapseContainer, classes.collapseContainerSecondary, {
                                                    [classes.collapseContainerCollapsed]: !isMenuOpen,
                                                }), in: isAnySubLinkActive(link.subLinks) || isSubCollapsed, children: _jsx(For, { of: link.subLinks, as: (link) => (_jsx(SubLink, { isCollapsed: isSubCollapsed, isMenuOpen: true, onClick: onTertiaryButtonClick, ...link })) }) })] }));
                                } }) }) }), _jsxs(Popover, { anchorEl: anchorEl, anchorOrigin: { vertical: 'top', horizontal: 'right' }, id: `popover-${id}`, open: popoverOpen, PaperProps: { square: true, className: classes.popoverPaper }, transformOrigin: { vertical: 'top', horizontal: 'left' }, onClose: onPopoverClose, children: [_jsx(Typography, { className: classes.subLinkHeader, children: text }), _jsx(List, { className: classes.popoverContent, disablePadding: true, children: _jsx(For, { of: subLinks, as: (link) => {
                                        return (_jsxs(_Fragment, { children: [_jsx(SubLink, { isCollapsed: isSubCollapsed, isMenuOpen: true, onClick: onPopoverClose, ...link }), _jsx(For, { of: link.subLinks, as: (link) => (_jsx(SubLink, { isCollapsed: isSubCollapsed, isMenuOpen: true, onClick: onTertiaryButtonClick, ...link })) })] }));
                                    } }) })] })] }))] }));
}
